/** tm/components/GovProgramExtras.tsx — 등록 양식에 덧붙는 세 조각 (2026-10-05 명세 「화면은 심플하게 · 내부는 정확하게」)
 *
 *  · RuleChipsRow      문장형 칩 한 줄 + 「＋ 조건」 — 「건설업 제외」「매출 10% 이상 감소」「FP 확인: 신용점수 839점 이하」
 *                      칩 누르기 = 그 자리 아래에 작은 편집 상자 · ✕ = 삭제 · 「또는」 묶음·낯선 키는 보기·지우기만
 *  · GovTracksSection  접힌 「세부 유형」 — 트랙마다 한 줄 카드(이름 · 금액 문구 · 만원 · 단위 · 조건 칩 · 삭제)
 *  · GovPreviewLine    「이 조건이면 우리 고객사 중 해당 N곳 · 확인 필요 M곳」 한 줄 (600ms 디바운스 · 실패해도 폼은 그대로)
 *
 *  편집 상자는 떠 있는 팝오버가 아니라 칩 줄 바로 아래에 펼친다 — 「매칭 조건 더보기」 상자가 overflow:hidden 이고 모바일 390px 에서도
 *  화면 밖으로 나가지 않게. 자부담·지원비율 등 meta 는 화면에 칸이 없고 객체째 보존만 한다.
 */
import { useEffect, useRef, useState } from 'react';
import type { GovConditions, GovPreviewResult, GovProgram, GovTrack, GovTrackUnit } from '../types';
import { C, R, T, IMEText } from './ui';
import * as api from '../api';
import { Pill } from './GovProgramForm';
import { parseManwon } from './GovFeedParts';
import { GOV_TAXONOMY } from './GovTaxonomy';
import {
  type EditKind, type RuleObj, type RuleChip, KIND_LABEL, OP_WORD, SCALE_CODES, SCALE_NAME, UNIT_OPTIONS,
  isEditKind, isObj, numOf, ruleChips, COND_ORDER, TRACK_RULE_ORDER,
} from './govRuleText';
/** 확장 조건 칩 줄에서 체크 칸으로 따로 보이는 키 (기술 기반 창업 — 2026-10-05l) */
const COND_CHIP_ORDER = COND_ORDER.filter(k => k !== 'tech');

// ── 공통 스타일 (기존 양식과 같은 크기 단계 11·12·13) ───────────────
const smallInput: React.CSSProperties = { padding: '6px 9px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', background: C.white, boxSizing: 'border-box', minWidth: 0 };
const hint: React.CSSProperties = { fontSize: T.xs, color: C.muted, lineHeight: 1.45 };
const miniBtn = (primary: boolean, disabled = false): React.CSSProperties => ({
  padding: '4px 12px', borderRadius: 6, fontSize: T.sm, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap',
  border: primary ? 'none' : `1px solid ${C.border}`, background: primary ? (disabled ? C.borderDk : C.accent) : C.white, color: primary ? C.white : C.sub,
});
const addBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 9px', borderRadius: 6, border: `1px dashed ${C.borderDk}`,
  background: C.white, color: C.accent, fontSize: T.xs, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
};

/** 공고 공통 「추가 조건」에서 고를 수 있는 것 */
export const COND_ADD_KINDS: EditKind[] = ['bizx', 'rgx', 'drop', 'exp', 'opened'];
/** 세부 유형 조건 — 자주 쓰는 판정 조건 먼저, 그다음 추가 조건 */
export const TRACK_ADD_KINDS: EditKind[] = ['emp', 'biz', 'scale', 'yrs', 'age', 'bizx', 'rgx', 'drop', 'exp', 'opened'];
/** 「FP 확인 항목」 빠른 입력 — 공고문에 자주 나오는 확인 사항 */
const CHK_SUGGEST = ['신용점수 839점 이하', '국세·지방세 체납 없음', '휴·폐업 아님', '신용관리 교육 이수', '동일 사업 중복 수혜 없음'];

// ═══ 편집 상자 ═══════════════════════════════════════════════════
interface Draft { list: string[]; a: string; b: string; op: string; text: string }
const s = (v: unknown): string => { const n = numOf(v); return n == null ? '' : String(n); };

function initDraft(kind: EditKind, v: unknown, chkText: string): Draft {
  const d: Draft = { list: [], a: '', b: '', op: '', text: '' };
  const o = isObj(v) ? v : {};
  switch (kind) {
    case 'bizx': case 'rgx': case 'biz':
      d.list = Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : typeof v === 'string' ? v.split(',').map(x => x.trim()).filter(Boolean) : []; break;
    case 'drop': d.a = s(o.pct); break;
    case 'exp': { d.op = o.op === '>=' ? '>=' : '<'; const u = numOf(o.usd); d.a = u == null ? '' : String(u / 10000); break; }
    case 'opened': d.a = typeof o.after === 'string' ? o.after.slice(0, 10) : ''; d.b = typeof o.before === 'string' ? o.before.slice(0, 10) : ''; break;
    case 'chk': d.text = chkText; break;
    case 'emp': d.op = typeof o.op === 'string' && OP_WORD[o.op] ? o.op : '>='; d.a = s(o.n); break;
    case 'scale': d.a = typeof v === 'string' ? v : ''; break;
    case 'yrs': case 'age': d.a = s(o.min); d.b = s(o.max); break;
  }
  return d;
}

type Built = { ok: true; value: unknown } | { ok: false; msg: string };
const DATE_RX = /^\d{4}-\d{2}-\d{2}$/;
/** 편집값 → 저장 모양 (null = 조건 지움). 서버(govTracks)와 같은 범위로 미리 막는다 */
function buildValue(kind: EditKind, d: Draft): Built {
  const num = (t: string) => (t.trim() === '' ? null : Number(t));
  switch (kind) {
    case 'bizx': case 'rgx': case 'biz': return { ok: true, value: d.list.length ? d.list : null };
    case 'drop': {
      const p = num(d.a);
      if (p == null) return { ok: true, value: null };
      if (!Number.isFinite(p) || p <= 0 || p > 100) return { ok: false, msg: '감소율은 0 초과 100 이하로 입력하세요' };
      return { ok: true, value: { pct: p } };
    }
    case 'exp': {
      const m = num(d.a);
      if (m == null) return { ok: true, value: null };
      if (!Number.isFinite(m) || m < 0) return { ok: false, msg: '수출실적을 숫자로 입력하세요' };
      return { ok: true, value: { op: d.op === '>=' ? '>=' : '<', usd: Math.round(m * 10000) } };
    }
    case 'opened': {
      const a = d.a.trim(), b = d.b.trim();
      if (!a && !b) return { ok: true, value: null };
      if ((a && !DATE_RX.test(a)) || (b && !DATE_RX.test(b))) return { ok: false, msg: '날짜를 고르세요' };
      if (a && b && a >= b) return { ok: false, msg: '「이후」 날짜가 「이전」 날짜보다 앞서야 합니다' };
      return { ok: true, value: { before: b || null, after: a || null } };
    }
    case 'chk': {
      const t = d.text.replace(/\s+/g, ' ').trim();
      if (t.length > 100) return { ok: false, msg: '100자 이내로 입력하세요' };
      return { ok: true, value: t || null };
    }
    case 'emp': {
      const n = num(d.a);
      if (n == null) return { ok: true, value: null };
      if (!Number.isInteger(n) || n < 0) return { ok: false, msg: '인원은 0 이상 정수로 입력하세요' };
      return { ok: true, value: { op: d.op, n } };
    }
    case 'scale': return { ok: true, value: d.a || null };
    case 'yrs': case 'age': {
      const lo = num(d.a), hi = num(d.b);
      const max = kind === 'age' ? 120 : 200;
      for (const x of [lo, hi]) {
        if (x == null) continue;
        if (!Number.isFinite(x) || x < 0 || x > max) return { ok: false, msg: `0~${max} 사이로 입력하세요` };
        if (kind === 'age' && !Number.isInteger(x)) return { ok: false, msg: '나이는 정수로 입력하세요' };
      }
      if (lo == null && hi == null) return { ok: true, value: null };
      if (lo != null && hi != null && lo > hi) return { ok: false, msg: '하한이 상한보다 큽니다' };
      const out: { min?: number; max?: number } = {};
      if (lo != null) out.min = lo;
      if (hi != null) out.max = hi;
      return { ok: true, value: out };
    }
  }
  return { ok: true, value: null };
}

function RuleEditor({ kind, value, chkText, isNew, bizOptions, regionOptions, onApply, onCancel }: {
  kind: EditKind; value: unknown; chkText: string; isNew: boolean;
  bizOptions: readonly string[]; regionOptions: readonly string[];
  onApply: (v: unknown) => void; onCancel: () => void;
}) {
  const [d, setD] = useState<Draft>(() => initDraft(kind, value, chkText));
  const built = buildValue(kind, d);
  const firstRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => { firstRef.current?.focus(); }, []);
  const apply = () => { if (built.ok) onApply(built.value); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); }
    else if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); e.stopPropagation(); apply(); }
  };
  const set = (p: Partial<Draft>) => setD(x => ({ ...x, ...p }));
  const toggle = (t: string) => setD(x => ({ ...x, list: x.list.includes(t) ? x.list.filter(y => y !== t) : [...x.list, t] }));
  const lbl: React.CSSProperties = { fontSize: T.xs, color: C.sub, whiteSpace: 'nowrap' };

  let body: React.ReactNode = null;
  if (kind === 'bizx' || kind === 'rgx' || kind === 'biz') {
    const base = kind === 'rgx' ? ['수도권', ...regionOptions] : bizOptions;
    const opts = [...base, ...d.list.filter(x => !base.includes(x))]; // AI 가 넣은 낯선 라벨도 보여서 뺄 수 있게
    const [bd, bg, fg] = kind === 'biz' ? ['#AFA9EC', '#EEEDFE', '#3C3489'] : ['#F2B8B5', '#FDECEA', '#8A1C14'];
    body = (
      <>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {opts.map(o => <Pill key={o} active={d.list.includes(o)} onClick={() => toggle(o)} activeBorder={bd} activeBg={bg} activeColor={fg}>{o}</Pill>)}
        </div>
        {kind === 'rgx' && <div style={{ ...hint, marginTop: 5 }}>수도권 = 서울·경기·인천</div>}
      </>
    );
  } else if (kind === 'drop') {
    body = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={lbl}>전년 대비 매출</span>
        <input ref={firstRef} type="number" min={0} max={100} step="any" value={d.a} onChange={e => set({ a: e.target.value })} placeholder="10" style={{ ...smallInput, width: 70 }} />
        <span style={lbl}>% 이상 감소</span>
      </div>
    );
  } else if (kind === 'exp') {
    body = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={lbl}>최근 1년 수출실적</span>
        <input ref={firstRef} type="number" min={0} step="any" value={d.a} onChange={e => set({ a: e.target.value })} placeholder="10" style={{ ...smallInput, width: 76 }} />
        <span style={lbl}>만 달러</span>
        <select value={d.op} onChange={e => set({ op: e.target.value })} style={smallInput}>
          <option value="<">미만</option><option value=">=">이상</option>
        </select>
      </div>
    );
  } else if (kind === 'opened') {
    body = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <input ref={firstRef} type="date" value={d.a} onChange={e => set({ a: e.target.value })} style={{ ...smallInput, width: 140 }} />
        <span style={lbl}>이후 ~</span>
        <input type="date" value={d.b} onChange={e => set({ b: e.target.value })} style={{ ...smallInput, width: 140 }} />
        <span style={lbl}>이전 개업</span>
        <div style={{ ...hint, flexBasis: '100%' }}>한쪽만 써도 됩니다 — 예: 「'25.1.1 이전 개업」은 오른쪽만</div>
      </div>
    );
  } else if (kind === 'chk') {
    body = (
      <>
        <input ref={firstRef} value={d.text} onChange={e => set({ text: e.target.value })} maxLength={100} placeholder="예: 신용점수 839점 이하"
          style={{ ...smallInput, width: '100%' }} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
          {CHK_SUGGEST.map(t => <Pill key={t} active={d.text === t} onClick={() => set({ text: t })} activeBorder="#9CA3AF" activeBg="#F3F4F6" activeColor="#374151">{t}</Pill>)}
        </div>
        <div style={{ ...hint, marginTop: 5 }}>판정에는 쓰지 않고 FP 화면에 「확인할 것」으로만 보입니다 · 리포트엔 안 나옵니다</div>
      </>
    );
  } else if (kind === 'emp') {
    body = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={lbl}>상시근로자</span>
        <input ref={firstRef} type="number" min={0} value={d.a} onChange={e => set({ a: e.target.value })} placeholder="5" style={{ ...smallInput, width: 70 }} />
        <span style={lbl}>명</span>
        <select value={d.op} onChange={e => set({ op: e.target.value })} style={smallInput}>
          {['<', '<=', '>=', '>'].map(o => <option key={o} value={o}>{OP_WORD[o]}</option>)}
        </select>
      </div>
    );
  } else if (kind === 'scale') {
    body = (
      <>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {SCALE_CODES.map(c => <Pill key={c} active={d.a === c} onClick={() => set({ a: d.a === c ? '' : c })} activeBorder="#85B7EB" activeBg="#E6F1FB" activeColor="#0C447C">{SCALE_NAME[c]}</Pill>)}
        </div>
        <div style={{ ...hint, marginTop: 5 }}>고른 규모까지 — 더 작은 규모는 포함</div>
      </>
    );
  } else if (kind === 'yrs' || kind === 'age') {
    const unit = kind === 'age' ? '세' : '년';
    body = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={lbl}>{kind === 'age' ? '대표 만' : '업력'}</span>
        <input ref={firstRef} type="number" min={0} step={kind === 'age' ? 1 : 0.5} value={d.a} onChange={e => set({ a: e.target.value })} placeholder="하한" style={{ ...smallInput, width: 70 }} />
        <span style={lbl}>~</span>
        <input type="number" min={0} step={kind === 'age' ? 1 : 0.5} value={d.b} onChange={e => set({ b: e.target.value })} placeholder="상한" style={{ ...smallInput, width: 70 }} />
        <span style={lbl}>{unit}</span>
      </div>
    );
  }

  const empty = built.ok && built.value == null;
  return (
    <div onKeyDown={onKey} style={{ marginTop: 6, padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.borderDk}`, background: C.white, boxShadow: '0 4px 14px rgba(17,24,39,0.08)' }}>
      <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, marginBottom: 7 }}>{KIND_LABEL[kind]} {isNew ? '추가' : '고치기'}</div>
      {body}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 9, flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: T.xs, color: built.ok ? C.muted : C.red }}>
          {'msg' in built ? built.msg : empty && !isNew ? '비우고 적용하면 이 조건을 지웁니다' : ''}
        </span>
        <button type="button" onClick={onCancel} style={miniBtn(false)}>취소</button>
        <button type="button" onClick={apply} disabled={!built.ok || (isNew && empty)} style={miniBtn(true, !built.ok || (isNew && empty))}>{isNew ? '추가' : '적용'}</button>
      </div>
    </div>
  );
}

// ═══ 칩 한 줄 ═══════════════════════════════════════════════════
function ChipView({ chip, editable, active, onEdit, onRemove }: { chip: RuleChip; editable: boolean; active: boolean; onEdit: () => void; onRemove: () => void }) {
  const tone = chip.bad ? { bd: '#F2B8B5', bg: C.redLt, fg: C.red }
    : chip.check ? { bd: C.borderDk, bg: C.white, fg: C.sub }
    : { bd: '#85B7EB', bg: '#E6F1FB', fg: '#0C447C' };
  return (
    <span title={chip.bad ? '저장할 수 없는 값일 수 있습니다 — 지우고 다시 넣으세요' : editable ? '눌러서 고치기' : '보기·지우기만 됩니다'}
      style={{
        display: 'inline-flex', alignItems: 'center', maxWidth: '100%', minWidth: 0, borderRadius: 6, fontSize: T.xs,
        border: `1px ${chip.check ? 'dashed' : 'solid'} ${active ? C.accent : tone.bd}`, background: tone.bg, color: tone.fg,
        boxShadow: active ? `0 0 0 2px ${C.accentLt}` : undefined,
      }}>
      <span onClick={editable ? onEdit : undefined}
        style={{ padding: '3px 2px 3px 9px', cursor: editable ? 'pointer' : 'default', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
        {chip.text}
      </span>
      <button type="button" onClick={onRemove} aria-label={`${chip.text} 삭제`}
        style={{ flexShrink: 0, border: 'none', background: 'transparent', color: 'inherit', opacity: 0.55, cursor: 'pointer', fontSize: T.xs, padding: '3px 7px 3px 5px', lineHeight: 1 }}>✕</button>
    </span>
  );
}

interface Editing { kind: EditKind; idx: number | null; isNew: boolean }

/** 규칙 객체(확장 조건 · 트랙 rules) 한 줄 — 칩 + 「＋ 조건」 + 그 자리 편집 상자 */
export function RuleChipsRow({ rules, onChange, addKinds, order, bizOptions, regionOptions, emptyText, addLabel = '＋ 조건' }: {
  rules: RuleObj | null; onChange: (next: RuleObj) => void; addKinds: readonly EditKind[]; order: readonly string[];
  bizOptions: readonly string[]; regionOptions: readonly string[];
  /** 칩이 하나도 없을 때 옆에 보이는 말 */
  emptyText?: string; addLabel?: string;
}) {
  const [editing, setEditing] = useState<Editing | null>(null);
  const [menu, setMenu] = useState(false);
  const r: RuleObj = rules || {};
  const chips = ruleChips(r, order);

  const setKey = (k: string, v: unknown) => {
    const next: RuleObj = { ...r };
    if (v == null) delete next[k]; else next[k] = v;
    onChange(next);
  };
  const chkList = (): string[] => (Array.isArray(r.chk) ? r.chk.filter((x): x is string => typeof x === 'string') : []);
  const remove = (c: RuleChip) => {
    setEditing(null);
    if (c.k === 'chk' && c.idx != null && Array.isArray(r.chk)) {
      const l = chkList().filter((_, i) => i !== c.idx);
      setKey('chk', l.length ? l : null);
    } else setKey(c.k, null);
  };
  const apply = (e: Editing, v: unknown) => {
    if (e.kind === 'chk') {
      const l = chkList();
      const t = typeof v === 'string' ? v : '';
      let next: string[];
      if (e.isNew || e.idx == null) next = t && !l.includes(t) ? [...l, t] : l;
      else next = t ? l.map((x, i) => (i === e.idx ? t : x)).filter((x, i, a) => a.indexOf(x) === i) : l.filter((_, i) => i !== e.idx);
      setKey('chk', next.length ? next : null);
    } else setKey(e.kind, v);
    setEditing(null);
  };
  const pick = (k: EditKind) => {
    setMenu(false);
    // 이미 있는 조건(FP 확인 항목 말고)은 새로 만들지 않고 그 칩을 고친다
    const exists = k !== 'chk' && r[k] != null && !chips.some(c => c.k === k && c.bad);
    setEditing({ kind: k, idx: null, isNew: !exists });
  };
  const edValue = editing ? r[editing.kind] : undefined;
  const edChk = editing && editing.kind === 'chk' && editing.idx != null ? chkList()[editing.idx] || '' : '';

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', minWidth: 0 }}>
        {chips.map(c => {
          const editable = !c.bad && isEditKind(c.k);
          const active = !!editing && !editing.isNew && editing.kind === c.k && (c.k !== 'chk' || editing.idx === c.idx);
          return <ChipView key={`${c.k}:${c.idx ?? ''}`} chip={c} editable={editable} active={active}
            onEdit={() => { setMenu(false); setEditing(active ? null : { kind: c.k as EditKind, idx: c.idx ?? null, isNew: false }); }}
            onRemove={() => remove(c)} />;
        })}
        {!chips.length && emptyText && <span style={{ ...hint, marginRight: 2 }}>{emptyText}</span>}
        <button type="button" onClick={() => { setEditing(null); setMenu(m => !m); }} aria-expanded={menu}
          style={{ ...addBtn, ...(menu ? { border: `1px solid ${C.accent}`, background: C.accentLt } : null) }}>{addLabel}</button>
      </div>
      {menu && (
        <div style={{ marginTop: 6, padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.bgAlt, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
          <span style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, marginRight: 2 }}>무엇을 넣을까요</span>
          {addKinds.map(k => (
            <button type="button" key={k} onClick={() => pick(k)}
              style={{ padding: '3px 9px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.white, color: C.text, fontSize: T.xs, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {KIND_LABEL[k]}{k !== 'chk' && r[k] != null ? ' ✓' : ''}
            </button>
          ))}
          <button type="button" onClick={() => setMenu(false)} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: C.muted, fontSize: T.xs, cursor: 'pointer' }}>닫기</button>
        </div>
      )}
      {editing && (
        <RuleEditor key={`${editing.kind}:${editing.idx ?? 'n'}:${editing.isNew}`} kind={editing.kind} value={editing.isNew ? undefined : edValue} chkText={edChk}
          isNew={editing.isNew} bizOptions={bizOptions} regionOptions={regionOptions}
          onApply={v => apply(editing, v)} onCancel={() => setEditing(null)} />
      )}
    </div>
  );
}

/** 공고 공통 「추가 조건」 — 기술 기반 창업 체크(2026-10-05l) + 칩 한 줄
 *  bizChosen = 업종을 직접 골랐으면 기술 기반 체크는 판정에 쓰이지 않는다(엔진 toRules — 업종 칸이 우선) → 흐리게 + 안내 */
export function ConditionsRow({ conditions, onChange, bizOptions, regionOptions, bizChosen = false }: {
  conditions: GovConditions | null; onChange: (c: GovConditions) => void; bizOptions: readonly string[]; regionOptions: readonly string[]; bizChosen?: boolean;
}) {
  const tech = conditions?.tech === true;
  const setTech = (on: boolean) => { const next: GovConditions = { ...(conditions || {}) }; if (on) next.tech = true; else delete next.tech; onChange(next); };
  return (
    <div style={{ minWidth: 0 }}>
      <label style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 6, marginBottom: 6, fontSize: T.sm, color: C.text, cursor: 'pointer', lineHeight: 1.45, opacity: bizChosen && tech ? 0.75 : 1 }}
        title="공고문에 「기술 기반 (예비)창업기업」처럼 적혀 있을 때 — 업종을 비워 두면 제조업 · 연구개발업 · IT/SW개발업 업체에만 추천합니다. 업종을 직접 고르면 그 업종이 우선입니다. 검토함이 본문으로 자동으로 켜기도 합니다">
        <input type="checkbox" checked={tech} onChange={e => setTech(e.target.checked)} style={{ accentColor: C.accent, marginTop: 2, flexShrink: 0 }} />
        <span>기술 기반 창업 <span style={{ color: C.muted }}>(제조·연구개발·SW 업종만)</span>{bizChosen && tech && <span style={{ display: 'block', fontSize: T.xs, color: C.muted }}>업종을 직접 골라 두어 그 업종으로 판정합니다 — 업종을 비우면 제조·연구개발·SW 한정</span>}</span>
      </label>
      <RuleChipsRow rules={conditions as RuleObj | null} onChange={n => onChange(n as GovConditions)} addKinds={COND_ADD_KINDS} order={COND_CHIP_ORDER}
        bizOptions={bizOptions} regionOptions={regionOptions} emptyText="없음" />
    </div>
  );
}
/** 화면에 보이는 확장 조건 칩 개수 (접힌 머리에 표시) */
export const conditionsCount = (c: GovConditions | null): number => ruleChips(c as RuleObj | null, COND_ORDER).length;

// ═══ 세부 유형 ═══════════════════════════════════════════════════
export const TRACK_MAX = 20;
const newTrackId = () => `n${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-3)}`;
export const blankTrack = (): GovTrack => ({ id: newTrackId(), name: '', amount: '', amount_max_manwon: null, unit: '', rules: {} });
/** 이름 없는 «빈» 트랙 (서버도 조용히 뺀다) */
const isBlank = (t: GovTrack) => !t.name.trim() && !t.amount.trim() && t.amount_max_manwon == null && !t.unit
  && !(isObj(t.rules) && Object.keys(t.rules).length) && !t.deadline && !t.apply_start && !t.first_come && !t.category && !t.form;
/** 리포트 칸 고르기 — 무상 지원금 세부 분류 이름(정책자금 제외) · '' = 사업 분류를 따른다 (서버 govTracks.TRACK_CATS 와 같다) */
const TRACK_CAT_OPTIONS: string[] = GOV_TAXONOMY.filter(g => !g.loan).flatMap(g => g.items);
const nameKey = (v: string) => v.replace(/\s+/g, '').toLowerCase();

function TrackCard({ t, n, dupName, onChange, onRemove, bizOptions, regionOptions }: {
  t: GovTrack; n: number; dupName: boolean; onChange: (next: GovTrack) => void; onRemove: () => void;
  bizOptions: readonly string[]; regionOptions: readonly string[];
}) {
  const noName = !t.name.trim() && !isBlank(t);
  const err = noName ? '이름을 입력하세요' : dupName ? '같은 이름의 세부 유형이 있습니다' : '';
  // 금액 문구를 고치면 만원 숫자도 따라간다 — 숫자를 손으로 고친 뒤에는 그대로 둔다(공고 검토함 표와 같은 규칙)
  const setAmount = (v: string) => {
    const prevAuto = parseManwon(t.amount);
    const follow = t.amount_max_manwon == null || t.amount_max_manwon === prevAuto;
    const m = parseManwon(v);
    onChange({ ...t, amount: v, amount_max_manwon: follow ? m : t.amount_max_manwon });
  };
  const dates = [t.first_come ? '선착순' : '', t.deadline ? `마감 ${String(t.deadline).slice(5, 10).replace('-', '.')}` : ''].filter(Boolean).join(' · ');
  return (
    <div style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${err ? '#F2B8B5' : C.border}`, background: C.white, minWidth: 0 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', minWidth: 0 }}>
        <span style={{ fontSize: T.xs, fontWeight: 700, color: C.muted, width: 16, textAlign: 'right', flexShrink: 0 }}>{n}</span>
        <IMEText value={t.name} onChange={v => onChange({ ...t, name: v })} maxLength={60} placeholder="유형 이름 (예: 전동지게차)" aria-label={`세부 유형 ${n} 이름`}
          style={{ ...smallInput, flex: '2 1 120px', fontWeight: 600, color: C.text, borderColor: err ? '#F2B8B5' : C.border }} />
        <IMEText value={t.amount} onChange={setAmount} maxLength={100} placeholder="금액 문구" aria-label={`세부 유형 ${n} 금액 문구`}
          style={{ ...smallInput, flex: '1 1 96px' }} />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          <input type="number" min={0} step={1} value={t.amount_max_manwon ?? ''} aria-label={`세부 유형 ${n} 금액(만원)`} placeholder="만원"
            onChange={e => { const d = e.target.value.replace(/[^\d]/g, ''); onChange({ ...t, amount_max_manwon: d ? Number(d) : null }); }}
            style={{ ...smallInput, width: 72 }} />
          <span style={{ fontSize: T.xs, color: C.sub }}>만원</span>
          <select value={t.unit} onChange={e => onChange({ ...t, unit: e.target.value as GovTrackUnit })} aria-label={`세부 유형 ${n} 금액 단위`} style={smallInput}>
            {UNIT_OPTIONS.map(u => <option key={u.v || 'none'} value={u.v}>{u.label}</option>)}
          </select>
        </span>
        <button type="button" onClick={onRemove} title="이 세부 유형 삭제" aria-label={`세부 유형 ${n} 삭제`}
          style={{ flexShrink: 0, width: 28, height: 28, marginLeft: 'auto', borderRadius: 6, border: `1px solid ${C.border}`, background: C.white, color: C.muted, fontSize: T.xs, cursor: 'pointer' }}>✕</button>
      </div>
      {/* 리포트 칸 · 컨설팅만 — 유형마다 다른 칸(혁신바우처 마케팅 유형 = 오프라인 · 컨설팅 유형은 뺀다). 손으로 고치면 AI 다시 읽기가 바꾸지 않는다 (2026-10-07f 지도사) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 6, paddingLeft: 22, minWidth: 0 }}>
        <span style={{ fontSize: T.xs, color: C.sub, flexShrink: 0 }}>리포트 칸</span>
        <select value={t.category || ''} aria-label={`세부 유형 ${n} 리포트 칸`} disabled={t.form === 'consult'}
          onChange={e => { const v = e.target.value; onChange({ ...t, category: v, cat_by: 'manual' }); }}
          style={{ ...smallInput, color: t.category ? C.text : C.muted }}>
          <option value="">사업 분류 따름</option>
          {TRACK_CAT_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: T.xs, color: C.sub, cursor: 'pointer' }}>
          <input type="checkbox" checked={t.form === 'consult'} aria-label={`세부 유형 ${n} 컨설팅만`}
            onChange={e => onChange({ ...t, form: e.target.checked ? 'consult' : '', cat_by: 'manual' })} />
          컨설팅·교육만 (리포트에 안 실림)
        </label>
        {t.cat_by === 'ai' && (t.category || t.form) && <span style={{ fontSize: T.xs, color: C.muted }}>AI 가 정함</span>}
        {t.cat_by === 'manual' && <span style={{ fontSize: T.xs, color: C.muted }}>직접 정함</span>}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 6, paddingLeft: 22, minWidth: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <RuleChipsRow rules={isObj(t.rules) ? t.rules : {}} onChange={rules => onChange({ ...t, rules })} addKinds={TRACK_ADD_KINDS} order={TRACK_RULE_ORDER}
            bizOptions={bizOptions} regionOptions={regionOptions} emptyText="공통 조건 그대로" />
        </div>
      </div>
      {(err || dates) && (
        <div style={{ paddingLeft: 22, marginTop: 4, fontSize: T.xs, color: err ? C.red : C.muted }}>{err || dates}</div>
      )}
    </div>
  );
}

/** 폼 아래쪽 접힌 「세부 유형」 — 트랙이 있으면 펼친 채로 시작 */
export function GovTracksSection({ tracks, onChange, bizOptions, regionOptions }: {
  tracks: GovTrack[] | null; onChange: (next: GovTrack[]) => void; bizOptions: readonly string[]; regionOptions: readonly string[];
}) {
  const list = tracks || [];
  const [open, setOpen] = useState<boolean>(() => list.length > 0);
  const names = list.map(t => nameKey(t.name)).filter(Boolean);
  const full = list.length >= TRACK_MAX;
  const add = () => { if (!full) { onChange([...list, blankTrack()]); setOpen(true); } };
  const remove = (i: number) => {
    const t = list[i];
    if (!isBlank(t) && !window.confirm(`세부 유형 「${t.name.trim() || `${i + 1}번째`}」을(를) 지울까요?`)) return;
    onChange(list.filter((_, j) => j !== i));
  };
  return (
    <div style={{ marginBottom: 12, border: `1px solid ${C.border}`, borderRadius: R.md, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ padding: '9px 12px', background: C.bgAlt, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: T.sm, fontWeight: 700, color: C.sub, minWidth: 0 }}>
          세부 유형{list.length > 0 && <span style={{ color: C.accent }}> {list.length}</span>}
          <span style={{ fontWeight: 400, color: C.muted }}> (대상·금액이 다른 유형이 있을 때만)</span>
        </span>
        <span style={{ fontSize: T.xs, color: C.muted, flexShrink: 0 }}>{open ? '▾ 접기' : '▸ 펼치기'}</span>
      </div>
      {open && (
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={hint}>리포트엔 업체에 해당하는 유형만 보입니다 · 유형 조건은 위 공통 조건보다 우선합니다</div>
          {list.map((t, i) => (
            <TrackCard key={t.id || i} t={t} n={i + 1} dupName={!!t.name.trim() && names.filter(x => x === nameKey(t.name)).length > 1}
              onChange={nt => onChange(list.map((x, j) => (j === i ? nt : x)))} onRemove={() => remove(i)}
              bizOptions={bizOptions} regionOptions={regionOptions} />
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={add} disabled={full} style={{ ...addBtn, ...(full ? { color: C.muted, cursor: 'default' } : null) }}>＋ 세부 유형</button>
            {full && <span style={hint}>최대 {TRACK_MAX}개까지 넣을 수 있습니다</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══ 미리보기 ═══════════════════════════════════════════════════

/** 저장 전 미리보기 한 줄 — 입력이 멈추고 600ms 뒤 서버 판정. 실패해도 폼에는 영향 없음 */
export function GovPreviewLine({ payload, onResult }: { payload: Partial<GovProgram>; onResult?: (r: GovPreviewResult) => void }) {
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const json = JSON.stringify(payload);
  const [state, setState] = useState<{ loading: boolean; data: GovPreviewResult | null; error: string }>({ loading: false, data: null, error: '' });
  const seq = useRef(0);
  useEffect(() => {
    const my = ++seq.current;
    setState(s0 => ({ ...s0, loading: true }));
    const timer = setTimeout(() => {
      api.previewGovProgram(JSON.parse(json) as Partial<GovProgram>)
        .then(r => {
          if (my !== seq.current) return;
          if (r.success && r.data && Array.isArray(r.data.samples)) { setState({ loading: false, data: r.data, error: '' }); if (onResultRef.current) onResultRef.current(r.data); }
          else setState(s0 => ({ loading: false, data: s0.data, error: r.error || '미리보기를 불러오지 못했습니다' }));
        })
        .catch((e: unknown) => {
          if (my !== seq.current) return;
          setState(s0 => ({ loading: false, data: s0.data, error: e instanceof Error && e.message ? e.message : '미리보기를 불러오지 못했습니다' }));
        });
    }, 600);
    return () => clearTimeout(timer);
  }, [json]);

  const { data, loading, error } = state;
  const stale = loading || !!error; // 지난 결과는 흐리게 — 지금 입력과 다를 수 있다
  // 2026-10-04 지도사 「이건 용도가 뭐야」 — 고정 샘플 4곳은 지역 공고에서 늘 「해당 없음」이라 뺐다. 우리 고객사 기준 한 줄만
  return (
    <div style={{ marginTop: 4, marginBottom: 4, padding: '8px 12px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.bgAlt, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: T.sm, fontWeight: 700, color: C.text }}>이 조건이면</span>
      {data ? (
        <span style={{ fontSize: T.sm, color: C.sub, opacity: stale ? 0.55 : 1 }}>
          우리 고객사 중 해당 <b style={{ color: C.green }}>{data.mine.pass.toLocaleString()}곳</b> · 확인 필요 <b style={{ color: C.orange }}>{data.mine.near.toLocaleString()}곳</b>
          <span style={{ color: C.muted }}> (전체 {data.mine.total.toLocaleString()}곳)</span>
        </span>
      ) : !error && <span style={hint}>{loading ? '계산 중…' : '입력하면 바로 셉니다'}</span>}
      {error && <span style={{ fontSize: T.xs, color: C.red, lineHeight: 1.45, wordBreak: 'keep-all' }}>{error}</span>}
    </div>
  );
}
